import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Mail, Loader2, AlertTriangle, Columns3, ChevronRight, Gift, Wallet,
  SlidersHorizontal, X as XIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  searchWorkers, getCompanyWorkers,
  getWorkplaces, listAgreements, searchCompanies,
} from '../services/api';
import { USER_TYPE_LABELS, filterWorkersForUser, canManageWorker, workerSites } from '../lib/permissions';
import {
  buildCompanyOptions, companyNameMap, companyLabel, matchesCompanyFilter,
} from '../utils/workerCompanies';
import AssignCompensationModal from '../components/AssignCompensationModal';
import {
  eligibleForBalance, formatBalance, balanceColor,
  NO_SCHEDULE_SHORT_LABEL, NO_SCHEDULE_ADMIN_HINT, formatNoScheduleBalance,
} from '../lib/hourBalance';
import { useLazyHourBalances } from '../hooks/useWorkerHourBalance';

// Chip de bolsa de una fila. Muestra el MISMO total que la ficha del
// trabajador y la app (automático + ajustes manuales). Calcularlo cuesta
// varias peticiones por trabajador, así que solo se pide cuando la fila entra
// en pantalla (IntersectionObserver); la cola compartida limita a 3
// trabajadores a la vez y cachea el resultado durante la sesión.
const BalanceChip = ({ worker, entry, onNeeded }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (entry) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') { onNeeded(worker); return undefined; }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) {
        obs.disconnect();
        onNeeded(worker);
      }
    }, { rootMargin: '200px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [worker, entry, onNeeded]);

  if (!entry || entry.status === 'loading') {
    return (
      <span ref={ref} className="inline-flex" title="Calculando bolsa…">
        <Loader2 size={12} className="animate-spin text-ink-muted/60" />
      </span>
    );
  }
  if (entry.status === 'error') {
    return <span ref={ref} className="text-ink-muted/60 italic text-xs" title="No se pudo calcular la bolsa">—</span>;
  }
  // Sin horario asignado: no hay número (antes salían +1175h falsas).
  if (entry.noSchedule) {
    return (
      <span
        ref={ref}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold italic bg-slate-100 text-ink-muted"
        title={`${formatNoScheduleBalance(entry.manual)}. ${NO_SCHEDULE_ADMIN_HINT}.`}
      >
        <Wallet size={10} /> {NO_SCHEDULE_SHORT_LABEL}
      </span>
    );
  }
  const color = balanceColor(entry.total);
  const cls = color === 'positive' ? 'bg-success/10 text-success'
            : color === 'negative' ? 'bg-danger/10 text-danger'
            : 'bg-slate-100 text-ink-muted';
  return (
    <span
      ref={ref}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${cls}`}
      title={`Bolsa total: automático ${formatBalance(entry.auto)} + ajustes manuales ${formatBalance(entry.manual)}`}
    >
      <Wallet size={10} /> {formatBalance(entry.total)}
    </span>
  );
};

const AVATAR_COLORS = [
  'bg-primary-soft text-primary-darker',
  'bg-accent-light text-accent-dark',
  'bg-success/15 text-success',
  'bg-warning/15 text-warning',
  'bg-danger/15 text-danger',
];

const initials = (nombre = '') =>
  nombre.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

const colorForId = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];

// Mismo mapeo que en WorkerModal/BulkImportModal: tres flags del backend
// proyectados en una sola opción de UI. Útil para filtrar por modo desde
// esta página.
const deduceFichajeMode = (w) => {
  if (!w) return null;
  if (w.enforceStartHour) return 'mixed';
  if (w.canRequestCheckins && w.overtimeAllowed) return 'free';
  return 'validation';
};
const FICHAJE_MODE_LABELS = {
  validation: 'Validación de jornada',
  free: 'Fichaje libre',
  mixed: 'Fichaje mixto',
};

const EMPTY_FILTERS = {
  companyId: 'todos',    // 'todos' | 'sin-empresa' | <id> — SOLO superadmin
  modality: 'todos',     // 'todos' | 'remoto' | 'presencial'
  workplaceId: 'todos',  // 'todos' | 'sin-sede' | <id>
  role: 'todos',         // 'todos' | <type-number>
  fichajeMode: 'todos',  // 'todos' | 'validation' | 'free' | 'mixed'
  agreementId: 'todos',  // 'todos' | 'sin-convenio' | <id>
  province: 'todos',     // 'todos' | <nombre>
  balance: 'todos',      // 'todos' | 'positive' | 'negative' | 'zero' | 'noSchedule'
};

const EquipoPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // tab: 'activos' | 'inactivos' — los inactivos son los que tienen login deshabilitado.
  const [tab, setTab] = useState('activos');
  const [workers, setWorkers] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [agreements, setAgreements] = useState([]);
  // Catálogo de empresas — solo se pide para el superadmin, que es el único
  // que ve trabajadores de varias empresas. Únicamente aporta NOMBRES: si la
  // petición falla, el filtro sigue funcionando con los ids de las filas.
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Saldo de bolsa de horas por trabajador: total automático + manual, igual
  // que la ficha. Carga perezosa (ver BalanceChip). Map id → { status, total }.
  const { balances, request: requestBalance } = useLazyHourBalances();
  // Modal de "regalar día libre" lanzado desde la fila del trabajador.
  const [compModal, setCompModal] = useState({ open: false, worker: null });

  const isMasterUser = user?.type === 5;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (isMasterUser || !user?.companyId)
        ? await searchWorkers('')
        : await getCompanyWorkers(user.companyId);
      setWorkers(filterWorkersForUser(Array.isArray(data) ? data : [], user));
    } catch (err) {
      setError(err);
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, isMasterUser]);

  useEffect(() => { load(); }, [load]);

  // Cargamos sedes y convenios para alimentar los selectores de filtro.
  // Si la empresa no tiene sedes, el selector queda oculto (no aporta).
  // Si listAgreements falla, dejamos el filtro de convenio vacío pero no
  // bloqueamos la página.
  useEffect(() => {
    if (!user?.companyId || isMasterUser) {
      setWorkplaces([]);
    } else {
      getWorkplaces(user.companyId)
        .then((list) => setWorkplaces(Array.isArray(list) ? list : []))
        .catch(() => setWorkplaces([]));
    }
    listAgreements()
      .then((list) => setAgreements(Array.isArray(list) ? list : []))
      .catch(() => setAgreements([]));
  }, [user?.companyId, isMasterUser]);

  // Empresas: una sola petición y SOLO para el superadmin (el resto ve una
  // única empresa, no hay nada que elegir). Misma función que usa Informes
  // para su selector multi-empresa. Si falla, lista vacía: las opciones del
  // filtro se derivan igualmente del companyId de cada fila.
  useEffect(() => {
    if (!isMasterUser) { setCompanies([]); return undefined; }
    let cancelled = false;
    searchCompanies('')
      .then((list) => { if (!cancelled) setCompanies(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setCompanies([]); });
    return () => { cancelled = true; };
  }, [isMasterUser]);

  const isActive = (w) => w.isLoginEnabled !== false;

  const tabWorkers = useMemo(
    () => workers.filter((w) => (tab === 'inactivos' ? !isActive(w) : isActive(w))),
    [workers, tab],
  );

  // El filtro por bolsa necesita el saldo de TODOS los trabajadores de la
  // pestaña (no solo de las filas visibles), así que al activarlo encolamos
  // el resto. La cola sigue limitada a 3 a la vez.
  useEffect(() => {
    if (filters.balance === 'todos') return;
    for (const w of tabWorkers) requestBalance(w);
  }, [filters.balance, tabWorkers, requestBalance]);

  const balanceFilterProgress = useMemo(() => {
    if (filters.balance === 'todos') return null;
    const eligibles = tabWorkers.filter(eligibleForBalance);
    const done = eligibles.filter((w) => {
      const e = balances.get(Number(w.id));
      return e && e.status !== 'loading';
    }).length;
    return { done, total: eligibles.length };
  }, [filters.balance, tabWorkers, balances]);

  // Lista de provincias presentes en los workers actuales — la generamos
  // dinámicamente para no mostrar opciones que nadie usa. Las ordenamos
  // alfabéticamente con locale español.
  const provinceOptions = useMemo(() => {
    const set = new Set();
    for (const w of workers) {
      if (w.province && String(w.province).trim()) set.add(String(w.province).trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [workers]);

  // Empresas presentes en las filas (con el nombre del catálogo cuando lo
  // hay). Se memoiza igual que provincias/roles: con plantillas grandes esto
  // recorre miles de filas y no debe repetirse en cada render.
  const companyNames = useMemo(() => companyNameMap(companies), [companies]);
  const companyOptions = useMemo(
    () => (isMasterUser ? buildCompanyOptions(workers, companies) : []),
    [isMasterUser, workers, companies],
  );

  // Lista de roles realmente presentes: lo mismo que con provincias.
  const roleOptions = useMemo(() => {
    const set = new Set();
    for (const w of workers) {
      if (w.type != null) set.add(Number(w.type));
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [workers]);

  // Convenios RELEVANTES: solo los que algún trabajador de esta empresa
  // tiene asociados. listAgreements() devuelve TODOS los del sistema (no
  // hay endpoint por empresa); aquí los filtramos para que el selector
  // solo muestre opciones realmente útiles. Esto cambia la UX en
  // empresas con plantilla muy específica: si nadie usa un convenio, no
  // tiene sentido mostrarlo como filtro.
  const relevantAgreements = useMemo(() => {
    if (!agreements.length) return [];
    const inUse = new Set();
    for (const w of workers) {
      if (w.agreement != null && w.agreement !== '') inUse.add(String(w.agreement));
    }
    return agreements.filter((a) => inUse.has(String(a.id)));
  }, [agreements, workers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tabWorkers.filter((w) => {
      // Búsqueda libre: amplía el haystack a teléfono y SS para no obligar
      // al admin a recordar exactamente por cuál campo busca.
      if (q) {
        const hay = `${w.name || ''} ${w.email || ''} ${w.nif || ''} ${w.phone || ''} ${w.ssNumber || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Empresa: solo el superadmin puede moverlo (para el resto vale
      // siempre 'todos', así que no cambia nada).
      if (!matchesCompanyFilter(w, filters.companyId)) return false;

      if (filters.modality === 'remoto' && !w.worksRemotely) return false;
      if (filters.modality === 'presencial' && w.worksRemotely) return false;

      if (filters.workplaceId !== 'todos') {
        const sites = workerSites(w); // multicentro: array de centros asignados
        if (filters.workplaceId === 'sin-sede') {
          if (sites.length) return false;
        } else if (!sites.includes(String(filters.workplaceId))) {
          return false;
        }
      }

      if (filters.role !== 'todos' && Number(w.type) !== Number(filters.role)) return false;

      if (filters.fichajeMode !== 'todos' && deduceFichajeMode(w) !== filters.fichajeMode) return false;

      if (filters.agreementId !== 'todos') {
        const aId = w.agreement != null ? String(w.agreement) : '';
        if (filters.agreementId === 'sin-convenio') {
          if (aId) return false;
        } else if (aId !== String(filters.agreementId)) {
          return false;
        }
      }

      if (filters.province !== 'todos' && String(w.province || '') !== filters.province) return false;

      if (filters.balance !== 'todos') {
        if (!eligibleForBalance(w)) return false;
        const entry = balances.get(Number(w.id));
        if (!entry || entry.status !== 'done') return false; // todavía calculando (o error)
        // Sin horario: grupo propio. Nunca entra en los filtros numéricos
        // (no es positivo, ni negativo, ni cero: no hay número).
        if (filters.balance === 'noSchedule') return !!entry.noSchedule;
        if (entry.noSchedule) return false;
        const sign = balanceColor(entry.total);
        if (filters.balance === 'positive' && sign !== 'positive') return false;
        if (filters.balance === 'negative' && sign !== 'negative') return false;
        if (filters.balance === 'zero' && sign !== 'neutral') return false;
      }

      return true;
    });
  }, [tabWorkers, query, filters, balances]);

  const inactiveCount = useMemo(() => workers.filter((w) => !isActive(w)).length, [workers]);
  const activeCount = workers.length - inactiveCount;
  // Porcentajes para la mini-barra de distribución (versión compacta de la
  // antigua tarjeta "Distribución de la plantilla"). Si no hay trabajadores,
  // 0 / 0 para evitar dar la falsa sensación de 100% activo.
  const totalCount = workers.length;
  const activePct = totalCount === 0 ? 0 : Math.round((activeCount / totalCount) * 100);
  const inactivePct = totalCount === 0 ? 0 : 100 - activePct;

  // Conteos para el subtítulo de KPIs (línea fina debajo del título — antes
  // ocupaban una sección entera con 4 grandes números, ahora son inline).
  const counts = useMemo(() => ({
    total: tabWorkers.length,
    remoto: tabWorkers.filter((w) => w.worksRemotely).length,
    presencial: tabWorkers.filter((w) => !w.worksRemotely).length,
    admins: tabWorkers.filter((w) => [1, 2].includes(w.type)).length,
  }), [tabWorkers]);

  // Indica si el usuario tiene filtros activos (distintos de los defaults).
  // Lo usamos para mostrar un contador junto al botón "Filtros" y un botón
  // de "Limpiar" cuando hay alguno aplicado.
  const activeFilterCount = useMemo(() => {
    let n = 0;
    for (const k of Object.keys(EMPTY_FILTERS)) {
      if (filters[k] !== EMPTY_FILTERS[k]) n += 1;
    }
    if (query.trim()) n += 1;
    return n;
  }, [filters, query]);

  const clearAll = () => {
    setQuery('');
    setFilters(EMPTY_FILTERS);
  };

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  // Lookup de sede por id — para mostrar el nombre en la tabla en vez del
  // id crudo. Caer al id en bruto si la sede ya no existe (workplaces aún
  // no cargados, o sede eliminada).
  const workplaceName = useCallback((id) => {
    if (id == null || id === '') return null;
    const wp = workplaces.find((w) => String(w.id) === String(id));
    return wp ? wp.name : null;
  }, [workplaces]);

  const showWorkplaceFilter = workplaces.length > 1;

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      {/* Cabecera compacta — antes el título ocupaba mucho espacio.
          Ahora va con KPIs inline a su derecha para que las métricas no
          requieran una sección dedicada. */}
      <div className="flex flex-wrap items-end justify-between mb-4 gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Equipo</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">
            {isMasterUser ? 'Todos los trabajadores' : 'Mi empresa'}
          </h1>
          {/* Distribución de plantilla en versión MINI: una barra finita
              de 2 colores + porcentajes inline. Reemplaza a la tarjeta
              grande de antes ("Distribución de la plantilla") y a los 4
              números grandes. Densa y discreta. */}
          {totalCount > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <div className="flex items-center gap-2 min-w-[140px]">
                <div className="flex h-1.5 w-24 rounded-full overflow-hidden bg-slate-200" aria-hidden>
                  {activePct > 0 && <div className="bg-success" style={{ width: `${activePct}%` }} />}
                  {inactivePct > 0 && <div className="bg-slate-300" style={{ width: `${inactivePct}%` }} />}
                </div>
                <span className="text-ink-muted tabular-nums">
                  <b className="text-ink">{activePct}%</b> activos
                </span>
              </div>
              <span className="text-slate-300">·</span>
              <span className="text-ink-muted tabular-nums"><b className="text-ink">{counts.admins}</b> admin{counts.admins === 1 ? '' : 's'}</span>
              <span className="text-slate-300">·</span>
              <span className="text-ink-muted tabular-nums"><b className="text-ink">{counts.remoto}</b> remoto</span>
              <span className="text-slate-300">·</span>
              <span className="text-ink-muted tabular-nums"><b className="text-ink">{counts.presencial}</b> presencial</span>
              {filtered.length !== tabWorkers.length && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-primary-darker tabular-nums font-semibold">
                    {filtered.length} mostrad{filtered.length === 1 ? 'o' : 'os'} de {tabWorkers.length}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => navigate('/panel/equipo/comparar')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-ink text-white hover:bg-slate-800 transition-colors shrink-0"
        >
          <Columns3 size={16} /> Comparar
        </button>
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-3 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-ink">{error.message}</div>
          </div>
        </div>
      )}

      {/* Tabs Activos / Inactivos — siguen siendo un primer corte importante
          (jurídicamente no quitamos trabajadores, los desactivamos). */}
      <div className="border-b border-slate-200 mb-3 flex items-center gap-1 shrink-0">
        {[
          { key: 'activos', label: 'Activos', count: activeCount },
          { key: 'inactivos', label: 'Inactivos', count: inactiveCount },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-primary text-primary-darker'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
            title={t.key === 'inactivos' ? 'Trabajadores con login deshabilitado' : 'Trabajadores con acceso habilitado'}
          >
            {t.label}
            <span className="ml-1 text-xs text-ink-muted tabular-nums">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Barra de filtros — todo en una sola fila densa para no robar
          espacio a la tabla. Los filtros menos usados se ocultan tras
          "Más filtros" para no ensuciar la vista por defecto. */}
      <div className="mb-3 shrink-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, email, NIF, teléfono…"
              className="w-full pl-9 pr-9 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-ink-muted hover:text-ink hover:bg-slate-100"
                title="Limpiar búsqueda"
              >
                <XIcon size={12} />
              </button>
            )}
          </div>

          {/* Empresa — solo el superadmin ve plantilla de varias empresas.
              Si todas las filas son de la misma, el selector no aporta. */}
          {isMasterUser && companyOptions.length > 1 && (
            <select
              value={filters.companyId}
              onChange={setFilter('companyId')}
              className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white max-w-[220px]"
              title="Filtrar por empresa"
            >
              <option value="todos">Empresa: todas</option>
              {companyOptions.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          )}

          {/* Selects compactos — los más útiles siempre visibles. */}
          <select
            value={filters.modality}
            onChange={setFilter('modality')}
            className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white"
            title="Filtrar por modalidad de trabajo"
          >
            <option value="todos">Modalidad: todas</option>
            <option value="remoto">En remoto</option>
            <option value="presencial">Presencial</option>
          </select>

          {showWorkplaceFilter && (
            <select
              value={filters.workplaceId}
              onChange={setFilter('workplaceId')}
              className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white max-w-[200px]"
              title="Filtrar por sede"
            >
              <option value="todos">Sede: todas</option>
              <option value="sin-sede">Sin sede asignada</option>
              {workplaces.map((wp) => (
                <option key={wp.id} value={wp.id}>{wp.name}</option>
              ))}
            </select>
          )}

          {roleOptions.length > 1 && (
            <select
              value={filters.role}
              onChange={setFilter('role')}
              className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white"
              title="Filtrar por tipo de usuario"
            >
              <option value="todos">Rol: todos</option>
              {roleOptions.map((t) => (
                <option key={t} value={t}>{USER_TYPE_LABELS[t] || `Tipo ${t}`}</option>
              ))}
            </select>
          )}

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              showAdvanced
                ? 'border-primary text-primary-darker bg-primary-soft'
                : 'border-slate-200 text-ink-muted hover:text-ink hover:bg-slate-50'
            }`}
            title="Mostrar más filtros"
          >
            <SlidersHorizontal size={14} />
            Más filtros
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold tabular-nums">
                {activeFilterCount}
              </span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-xs font-semibold text-ink-muted hover:text-danger px-2 py-1 rounded transition-colors"
              title="Limpiar todos los filtros y la búsqueda"
            >
              <XIcon size={12} /> Limpiar
            </button>
          )}
        </div>

        {/* Panel avanzado — fichaje, convenio, provincia, bolsa. Solo se
            renderiza si el admin pinchó "Más filtros". Si no aporta ningún
            select útil (sin convenios, sin provincias) el panel se ve
            vacío — eso es OK, le dice al admin que no hay nada que filtrar. */}
        {showAdvanced && (
          <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2">
            <select
              value={filters.fichajeMode}
              onChange={setFilter('fichajeMode')}
              className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white"
              title="Filtrar por tipo de fichaje"
            >
              <option value="todos">Tipo de fichaje: todos</option>
              {Object.entries(FICHAJE_MODE_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>

            {relevantAgreements.length > 0 && (
              <select
                value={filters.agreementId}
                onChange={setFilter('agreementId')}
                className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white max-w-[220px]"
                title="Filtrar por convenio aplicado a algún trabajador de la empresa"
              >
                <option value="todos">Convenio: todos</option>
                <option value="sin-convenio">Sin convenio</option>
                {relevantAgreements.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            )}

            {provinceOptions.length > 1 && (
              <select
                value={filters.province}
                onChange={setFilter('province')}
                className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white"
                title="Filtrar por provincia"
              >
                <option value="todos">Provincia: todas</option>
                {provinceOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            )}

            <select
              value={filters.balance}
              onChange={setFilter('balance')}
              className="px-2.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary bg-white"
              title="Filtrar por estado de la bolsa de horas (automático + ajustes manuales)"
            >
              <option value="todos">Bolsa: cualquiera</option>
              <option value="positive">Bolsa positiva (a favor)</option>
              <option value="negative">Bolsa negativa (en contra)</option>
              <option value="zero">Bolsa a cero</option>
              <option value="noSchedule">Sin horario asignado</option>
            </select>
            {balanceFilterProgress && balanceFilterProgress.done < balanceFilterProgress.total && (
              <span className="self-center inline-flex items-center gap-1 text-xs text-ink-muted">
                <Loader2 size={11} className="animate-spin" />
                Calculando bolsa {balanceFilterProgress.done}/{balanceFilterProgress.total}…
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabla — ocupa todo el espacio restante. Antes había una sección
          gigante de KPIs entre los tabs y la tabla; ahora la tabla se ve
          desde la primera línea y scrollea por dentro. */}
      <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-ink-muted">
            <Loader2 size={18} className="animate-spin text-primary" />
            <span className="text-sm">Cargando equipo…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm text-ink-muted">
            {workers.length === 0
              ? 'No hay trabajadores todavía.'
              : (
                <>
                  Ningún miembro coincide con los filtros.
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="ml-2 underline text-primary-darker hover:text-primary"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </>
              )}
          </div>
        ) : (
          <table className="w-full min-w-[820px]">
            <thead className="sticky top-0 bg-background z-10">
              <tr className="text-xs uppercase tracking-wider text-ink-muted">
                <th className="text-left font-semibold py-3 pr-4">Persona</th>
                {isMasterUser && (
                  <th className="text-left font-semibold py-3 pr-4 hidden lg:table-cell">Empresa</th>
                )}
                <th className="text-left font-semibold py-3 pr-4 hidden md:table-cell">Rol</th>
                {workplaces.length > 0 && (
                  <th className="text-left font-semibold py-3 pr-4 hidden lg:table-cell">Sede</th>
                )}
                <th className="text-left font-semibold py-3 pr-4">Modalidad</th>
                <th className="text-left font-semibold py-3 pr-4 hidden md:table-cell">Bolsa</th>
                <th className="text-right font-semibold py-3 pr-4 w-10"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filtered.map((w) => (
                <tr
                  key={w.id}
                  onClick={() => navigate(`/panel/equipo/${w.id}`)}
                  className="border-t border-slate-100 hover:bg-white transition-colors cursor-pointer"
                >
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${colorForId(w.id)}`}>
                        {initials(w.name)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-ink truncate">{w.name || '—'}</div>
                        <div className="text-xs text-ink-muted truncate">{w.email}</div>
                        {/* Los nombres se repiten entre empresas: por debajo
                            de lg no hay columna, así que la empresa va aquí
                            como segunda línea. */}
                        {isMasterUser && (
                          <div className="text-xs text-ink-muted/80 truncate lg:hidden">
                            {companyLabel(w, companyNames)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  {isMasterUser && (
                    <td className="py-2.5 pr-4 text-ink-muted hidden lg:table-cell max-w-[200px]">
                      <span className="block truncate" title={companyLabel(w, companyNames)}>
                        {companyLabel(w, companyNames)}
                      </span>
                    </td>
                  )}
                  <td className="py-2.5 pr-4 text-ink-muted hidden md:table-cell">
                    {w.type != null ? USER_TYPE_LABELS[w.type] : <span className="text-ink-muted/60 italic">sin asignar</span>}
                  </td>
                  {workplaces.length > 0 && (
                    <td className="py-2.5 pr-4 text-ink-muted hidden lg:table-cell">
                      {(() => {
                        const name = workplaceName(w.workingSite);
                        if (name) return <span className="text-ink">{name}</span>;
                        return <span className="text-ink-muted/60 italic">sin asignar</span>;
                      })()}
                    </td>
                  )}
                  <td className="py-2.5 pr-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                      w.worksRemotely ? 'bg-success/10 text-success' : 'bg-primary-soft text-primary-darker'
                    }`}>
                      {w.worksRemotely ? 'Remoto' : 'Presencial'}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 hidden md:table-cell">
                    {(() => {
                      if (!eligibleForBalance(w)) {
                        return <span className="text-ink-muted/60 italic text-xs" title="No aplica — sin horas extra ni fichaje antes de hora">—</span>;
                      }
                      return (
                        <BalanceChip
                          worker={w}
                          entry={balances.get(Number(w.id))}
                          onNeeded={requestBalance}
                        />
                      );
                    })()}
                  </td>
                  <td className="py-2.5 pr-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      {w.email && (
                        <a
                          href={`mailto:${w.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 rounded-lg text-ink-muted hover:text-primary hover:bg-slate-100 transition-colors"
                          title={`Escribir a ${w.name}`}
                        >
                          <Mail size={16} />
                        </a>
                      )}
                      {canManageWorker(user, w) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCompModal({ open: true, worker: w }); }}
                          className="p-2 rounded-lg text-ink-muted hover:text-violet-600 hover:bg-violet-50 transition-colors"
                          title={`Asignar día de compensación a ${w.name}`}
                        >
                          <Gift size={16} />
                        </button>
                      )}
                      <ChevronRight size={16} className="text-ink-muted" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AssignCompensationModal
        open={compModal.open}
        worker={compModal.worker}
        onClose={() => setCompModal({ open: false, worker: null })}
        onSubmitted={() => {
          // Un día de compensación cambia la bolsa (ese día no se espera
          // jornada): recalculamos el total de ese trabajador.
          const w = compModal.worker;
          load();
          if (w) requestBalance(w, { force: true });
        }}
      />
    </div>
  );
};

export default EquipoPage;
